import { Head, Link, router, usePage } from '@inertiajs/react';

import { trackUmamiEvent } from '@/lib/analytics';
import { PARISH_TIME_ZONE } from '@/lib/datetime';

export type GallerySummary = {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  publishedAt: string | null;
  eventId: number;
  eventTitle: string;
  itemCount: number;
  coverUrl: string | null;
};

type Props = {
  galleries: GallerySummary[];
  [key: string]: unknown;
};

const dateFormatter = new Intl.DateTimeFormat('pt-PT', {
  dateStyle: 'medium',
  timeZone: PARISH_TIME_ZONE,
});

function formatDate(isoString: string | null): string {
  if (!isoString) return '—';
  return dateFormatter.format(new Date(isoString));
}

export default function Galleries() {
  const { galleries } = usePage<Props>().props;

  function handleDelete(gallery: GallerySummary) {
    if (
      !window.confirm(`Eliminar a galeria "${gallery.title}"? Esta ação não pode ser desfeita.`)
    ) {
      return;
    }
    trackUmamiEvent('admin_content_delete', { content_type: 'gallery' });
    router.delete(`/galleries/${gallery.id}`, { preserveScroll: true });
  }

  return (
    <>
      <Head title="Galerias — Administração" />
      <div className="space-y-5">
        <div className="flex items-center justify-between">
          <h1 className="font-headline text-on-surface text-2xl font-bold">Galerias</h1>
          <Link
            href="/galleries/new"
            className="bg-primary hover:bg-primary-dark inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold text-white transition-colors duration-200"
          >
            <span className="material-symbols-outlined text-[18px]">add</span>
            Nova galeria
          </Link>
        </div>

        {galleries.length === 0 ? (
          <div className="surface-card text-on-surface-light flex flex-col items-center gap-2 py-12 text-center text-sm">
            <span className="material-symbols-outlined text-on-surface-light/40 text-3xl">
              photo_library
            </span>
            Ainda não existem galerias publicadas ou em preparação.
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {galleries.map((gallery) => (
              <article key={gallery.id} className="surface-card overflow-hidden">
                <div className="bg-background border-line aspect-[16/9] border-b">
                  {gallery.coverUrl ? (
                    <img
                      src={gallery.coverUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  ) : (
                    <div className="text-on-surface-light/50 flex h-full items-center justify-center">
                      <span className="material-symbols-outlined text-4xl">photo_library</span>
                    </div>
                  )}
                </div>
                <div className="space-y-3 p-4">
                  <div>
                    <p className="text-secondary text-[10px] font-bold tracking-[0.12em] uppercase">
                      {gallery.eventTitle}
                    </p>
                    <h2 className="font-headline text-on-surface mt-1 text-xl font-semibold">
                      {gallery.title}
                    </h2>
                  </div>
                  <div className="text-on-surface-light flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                    <span>{formatDate(gallery.publishedAt)}</span>
                    <span className="bg-line-strong h-1 w-1 rounded-full" />
                    <span>{gallery.itemCount} itens</span>
                  </div>
                  <div className="border-line flex items-center justify-end gap-1 border-t pt-2">
                    <Link
                      href={`/galleries/${gallery.id}/edit`}
                      aria-label="Editar galeria"
                      className="text-on-surface-light hover:bg-primary/10 hover:text-primary rounded-md p-1.5 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[18px]">edit</span>
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(gallery)}
                      aria-label="Eliminar galeria"
                      className="text-on-surface-light hover:bg-error/10 hover:text-error rounded-md p-1.5 transition-colors"
                    >
                      <span className="material-symbols-outlined text-[18px]">delete</span>
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
